import { z } from 'zod';
import { EmailSchema, MoneyCentsSchema } from './shared.js';
/**
 * RIG-369 — rep-facing streak-status read model (Q4).
 *
 * A READ-ONLY snapshot of a single rep's current month-to-date commission +
 * SO-001 streak state. Computed live (no statement is written) and reconciles
 * byte-for-byte with payout because it runs the SAME `computeMonthlyRepCommission`
 * engine the monthly run uses. Backend builder: `getRepStreakStatus`.
 *
 * Presentation rule (RIG-363 C3d / no-"1%"): the component receives computed
 * CENTS + a `blendedEffectiveBps` rate — never a raw "1%" flag — so the literal
 * rate can never leak onto a rep-facing surface.
 */
/** One marginal-tier slice of the MTD base commission (for the tap/expand audit view). */
export const StreakTierRowSchema = z.object({
    lowerCents: MoneyCentsSchema,
    upperCents: MoneyCentsSchema.nullable(),
    rateBps: z.number().int().nonnegative(),
    sliceCents: MoneyCentsSchema,
    amountCents: MoneyCentsSchema,
});
/**
 * - `BUILDING`     — the streak bonus does NOT apply this month (prior 3 months
 *                    did not all qualify). The rep is building toward it.
 * - `ACTIVE`       — the bonus applies this month AND this month is on pace
 *                    (MTD gross ≥ threshold), so the streak continues next month.
 * - `BROKE_THIS_MONTH` — the bonus still applies this month (earned), but MTD
 *                    gross is below threshold, so the streak resets next month.
 */
export const StreakStateSchema = z.enum(['BUILDING', 'ACTIVE', 'BROKE_THIS_MONTH']);
export const RepStreakStatusSchema = z.object({
    repEmail: EmailSchema,
    /** Central (business-calendar) `YYYY-MM` of the in-progress month (RIG-654). */
    currentMonth: z.string().regex(/^\d{4}-\d{2}$/),
    /** MTD new-deal revenue (payment-received basis) — the streak/base input. */
    mtdGrossCents: MoneyCentsSchema,
    mtdBaseCents: MoneyCentsSchema,
    mtdStreakBonusCents: MoneyCentsSchema,
    mtdTotalCommissionCents: MoneyCentsSchema,
    /** base+streak ÷ gross, in basis points; display as ONE decimal percent. 0 when gross is 0. */
    blendedEffectiveBps: z.number().int().nonnegative(),
    streakState: StreakStateSchema,
    streakActiveNow: z.boolean(),
    /** Trailing run-length (0..3) of completed prior months that hit the threshold. */
    qualifyingConsecutiveMonths: z.number().int().min(0),
    /** Prior 3 completed months' new-deal revenue, oldest-first — for the 3-step visual. */
    priorMonthsRevenueCents: z.array(MoneyCentsSchema),
    /**
     * RIG-363 C2 — whether this rep has EVER earned a streak bonus before. Drives
     * "building toward your first Streak Bonus" vs "rebuilding after a break" copy.
     */
    hasEarnedStreakBefore: z.boolean(),
    qualifyThresholdCents: MoneyCentsSchema,
    /** max(0, threshold − mtdGross) — drives the progress bar in ALL states. */
    remainingToThresholdCents: MoneyCentsSchema,
    tierBreakdown: z.array(StreakTierRowSchema),
});
//# sourceMappingURL=streak-status.js.map